// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon } from "@fluentui/react";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useThemeColors } from "theming";

export interface IconDescriptor {
    name: dgIconNames | string;
    height: number;
    width: number;
    defaultColor?: string;
    shadow?: string;
    backgroundColor?: string;
    strokeColor?: string;
    isImage?: boolean;
    title?: string;
}

export interface BasicIconProps {
    name: dgIconNames;
    height: number;
    width: number;
}

export interface ColorIconProps extends BasicIconProps {
    color: string;
}

const IconContainer = styled.div<{ width: number, height: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${props => props.width}px;
    height: ${props => props.height}px;
`

export interface IconRendererProps {
    source: IconDescriptor;
    width: number;
    height: number;
    containerWidth?: number;
    containerHeight?: number;
    fallbackColor?: string;
    className?: string;
}

interface Dimensions {
    width: number;
    height: number;
}

export const IconRenderer = (props: IconRendererProps): JSX.Element => {
    const [iconWidth, setWidth] = useState(0);
    const [iconHeight, setHeight] = useState(0);

    const icons = useDgIcons();
    const themeColors = useThemeColors();

    useEffect(() => {
        const widthToHeight = props.source.width / props.source.height;
        const heightToWidth = 1 / widthToHeight;

        const heightConstrainedDim: Dimensions = {
            width: widthToHeight * props.height,
            height: props.height
        }

        const widthConstrainedDim: Dimensions = {
            width: props.width,
            height: props.width * heightToWidth
        }

        if (heightConstrainedDim.width <= props.width) {
            setWidth(heightConstrainedDim.width);
            setHeight(heightConstrainedDim.height);
        }
        else {
            setWidth(widthConstrainedDim.width);
            setHeight(widthConstrainedDim.height);
        }
    }, [props.height, props.width, props.source.width, props.source.height]);


    function getIcon(): JSX.Element {
        const themedPrimaryColor = themeColors.primary;
        if (typeof props.source.name === "string") {
            if (props.source.isImage) {
                // Image
                return <img src={props.source.name as string} width={iconWidth} height={iconHeight} alt='' />
            } else {
                // Fluent icon
                return <Icon iconName={props.source.name} title={props.source.title} styles={{ root: { fontSize: iconHeight, color: props.source.defaultColor ?? props.fallbackColor ?? themedPrimaryColor } }} />
            }
        }
        else {
            // Icon
            return icons.icon(
                props.source.name,
                props.source.title ?? "",
                iconHeight,
                iconWidth,
                props.source.defaultColor ?? props.fallbackColor ?? themedPrimaryColor,
                props.source.shadow ?? '',
                props.source.backgroundColor,
                props.source.strokeColor
            );
        }
    }

    return (
        <IconContainer className={props.className} width={props.containerWidth ?? iconWidth} height={props.containerHeight ?? iconHeight}>
            {getIcon()}
        </IconContainer>
    );
}
